import { create } from 'apisauce';
import { RequestErrorHandlerStore } from '.';
import { MILE_TO_METER } from '../constants/common';

const createApi = (baseURL, { noRedirectOn401 = false } = {}) => {
  const instance = create({ baseURL, withCredentials: true });

  instance.axiosInstance.interceptors.response.use(null, (error) => {
    const response = error.response;

    if (response?.status === 401 && !noRedirectOn401 && !response?.headers?.['no-redirect']) {
      window.location.href = `${process.env.REACT_APP_OAUTH_LOGIN_URL}?next=${encodeURI(window.location.href)}`;
      return;
    }

    if (response?.status === 403) RequestErrorHandlerStore.error(response);
    return error;
  });

  return instance;
};

export const api = createApi(`${process.env.REACT_APP_API_ROOT}`);
export const ws2Api = createApi(`${process.env.REACT_APP_WS2_API}`, { noRedirectOn401: true });
export const readonlyApi = createApi(`${process.env.REACT_APP_READONLY_API_ROOT || process.env.REACT_APP_API_ROOT}`);

export const dashboardAPI = createApi(`${process.env.REACT_APP_API_DASHBOARD}`);
export const paymentAPI = createApi(`${process.env.REACT_APP_API_PAYMENT}`);
export const routingAPI = createApi(`${process.env.REACT_APP_API_ROUTING}`);
export const trafficAPI = createApi(`${process.env.REACT_APP_API_TRAFFIC}`);
export const linehaulAPI = createApi(process.env.REACT_APP_LINEHAUL_API_URL);

const defaultVehicle = {
  capacity: 30,
  availability: 1,
  deploy_cost: 12345,
  loopback_allowed: false,
  max_assignment_duration: 72000,
  travel_distance_unit_cost: MILE_TO_METER,
  max_travel_time: 28800,
  travel_time_unit_cost: 20,
  service_time_unit_cost: 20,
  wait_time_unit_cost: 800,
  id: 1,
  name: 'Vehicle-1',
};

export const orderShipment = (id, data) => api.post(`/assignments/${id}/reorder-shipments`, data);
export const resetAssignmentOrder = (id, data) => api.post(`/assignments/${id}/reset-sequence-id`, data);
export const sendTrackingLink = (id) => api.post(`/shipments/${id}/sms/tracking_link`);
export const sendSignatureLink = (id) => api.post(`/shipments/${id}/sms/signature_link`);
export const getClientList = (size, page) => api.get(`/clients`, { size, page });

// for frankenroute
export const getBonusConfig = () => api.get(`/assignments/bonus/config`);
export const listShipmentsByField = (field, data) => api.post(`/shipments/list-with-params`, data, { params: { field, detail: true } });
export const getWarehouses = (params) => routingAPI.get(`/warehouses/list`, params);
export const listProblems = (owner_only, parent_only) => routingAPI.get(`/routing/problems/list`, { type: 'FRANKENROUTE', owner_only, parent_only });
export const getProblem = (id) => routingAPI.get(`/routing/problems/${id}`);
export const getSubProblems = (id) => routingAPI.get(`/routing/problems/${id}/children`);
export const getProblemConfig = (id) => routingAPI.get(`/labels/${id}/config`);
export const createProblem = (params) =>
  routingAPI.post(`/routing/problems/create_custom`, { ...params, source: 'frankenroute' }, { params: { validate_shipment: false } });
export const updateProblemStatus = (id, status) =>
  routingAPI.put(`/routing/problems/${id}/status`, status, { headers: { 'Content-Type': 'text/plain' } });
export const updateTimeWindow = (id, start, end) => routingAPI.put(`/routing/problems/${id}/timewindow`, [start, end]);
export const clusterZone = (id, shipmentZones) => routingAPI.put(`/routing/problems/${id}/cluster`, shipmentZones);
export const configLabel = (id, params) => routingAPI.post(`/labels/${id}/config`, params);
export const splitZones = (id) => routingAPI.post(`/routing/problems/${id}/calibrate-region`); // will return sub problems
export const addVehicle = (subId, capacity, availability) =>
  routingAPI.put(`/routing/problems/${subId}/vehicles`, [{ ...defaultVehicle, capacity, availability }]);
export const startRouting = (subId) => routingAPI.post(`/routing/problems/${subId}/reroute`);
export const getSolution = (id) => routingAPI.get(`/routing/solutions/${id}`);
export const selectProblemSolution = (id, keep_label) =>
  routingAPI.post(`/routing/problems/${id}/select-solution`, null, { params: { reset_dropoff_time: true, keep_label } });
export const getSelectedSolution = (id) => routingAPI.get(`/routing/problems/${id}/selected-solution-new`);
export const getSelectedSolutionRoutes = (solutionId) => routingAPI.get(`/routing/solutions/${solutionId}/assignments`);
export const getRoutePricingInfo = (problem_id, departure_date) =>
  routingAPI.post(`/routing/problems/simulation/${problem_id}/recalculate`, null, { params: { departure_date } });
export const updateBonus = (assignmentId, bonus) =>
  api.post(`/assignments/${assignmentId}/bonus`, { bonus, reason: 'Frankenroute bonus', reason_code: 'Frankenroute' });
export const getDriverCrewsAndBookingSessions = (driverId) => api.get(`/drivers/${driverId}/optional-removal-info`);
export const removeDriver = (driverId, params, data) => api.post(`/driver-crews/drivers/${driverId}`, data, { params });
export const getBoundaryFromRegions = (strRegions) => routingAPI.get(`/routing/zones/regions/${strRegions}`);
export const getClientSetting = (clientId) => dashboardAPI.get(`clients/${clientId}/settings`);
export const getLinehauls = (payload) => api.get(`/linehauls`, payload);
export const getDestinations = () => api.get(`/linehauls/destinations`);
export const getTypes = () => api.get(`/linehauls/types`);
export const getOrigins = () => api.get(`/linehauls/origins`);
export const getNotesBillingDisposition = (id) => api.get(`/notes/${id}`);
export const addNoteBillingDisposition = (id, content) => api.post(`/notes/${id}`, content, { headers: { 'Content-Type': 'text/plain' } });
export const editNoteBillingDisposition = (id, content) => api.put(`/notes/${id}/edit`, content);
export const getClientSuccessOwner = () => dashboardAPI.get(`/billing-dispositions/client-managements`);
export const getRegionList = () => api.get('/regions');
export const updatePickupEnforce = (bookingSessionId, ticketId, payload) =>
  api.put(`/tickets/booking-sessions/${bookingSessionId}/ticket-book/${ticketId}/pickup-enforce`, payload);
export const getEventTemplate = (targets, owners) => api.get('/event-template', { targets, owners });
export const getShipmentEvent = (id) => api.get(`/events/shipments/${id}?ref=true&rel=true`);
export const getShipmentEddEvents = (shipmentId) => api.get(`/events/shipments/${shipmentId}/edd`);
export const getShipmentWebhookEvents = (shipmentId) => api.get(`/webhooks/SH_${shipmentId}`);
export const assignmentAddShipment = (assignmentId, shipment_id, is_update_timewindow) =>
  api.post(`/assignments/${assignmentId}/add-shipment`, { shipment_id, is_update_timewindow });
export const redeliveryMultiple = (data, is_update_timewindow) => api.post('/redelivery/redelivery/multiple', { data, is_update_timewindow });
export const redeliveryMultipleV2 = (data, is_update_timewindow) => api.post('/redelivery/redelivery/multiple/v2', { data, is_update_timewindow });
export const getEventAssignments = (assignmentId) => api.get(`/events/assignments/${assignmentId}?ref=true`);
export const getTicketBookingSession = (regions) => api.get(`/driver-services/ticket-booking-session/list-by-regions`, { regions });
export const getDriverMatchBookingSession = (driverId) => api.get(`/driver-services/drivers/${driverId}/list-booking-sessions`);
export const getDirectBookingSession = (regions) => api.get(`/driver-services/direct-booking-session/list-by-regions`, { regions });
export const getBookingInfo = (assignmentId) => api.get(`/assignments/${assignmentId}/booking-info`);
export const getAssignmentInfo = (assignmentId) => api.get(`/assignments/${assignmentId}/info`);
export const saveTicketBooking = (ticketBookingId, bookingSessionId, ids) =>
  api.post(`/booking/ticket-book/${ticketBookingId}?session=${bookingSessionId}`, { ids });
export const saveDirectBooking = (directBookingId, ids) => api.post(`/driver-schedules/${directBookingId}/assignments/all`, { ids });
export const removeSchedule = (announcementId) => api.post(`/driver-announcements/${announcementId}/remove-schedule`);
export const getDriverScheduleSettings = () => api.get(`/driver-schedules/settings`);
export const getAppFeatures = (owners) => api.get(`/app-features/filtered`, { owners });

export const getReport = (id) => api.get(`/reports/uploaded/${id}`);
export const requestReport = (type, payload) => api.get(`/reports/${type}/request-report`, payload);
export const getAssignmentsActive = (driverId) => api.get(`assignments/active/${driverId}`);
export const searchNearestAssignment = (payload) => api.post(`/redelivery/search-nearest-assignment`, payload);
export const getEvents = (eventId, urlParams) => api.get(`/events/${eventId}/all`, urlParams);
export const getEventsByCategoryType = (category, type, urlParams) => api.get(`/events/cat/${category}/type/${type}`, urlParams);
export const getRegionBonusSettings = () => api.get('/bonus-ineligible/settings');
export const turnOffRegionBonus = (regionCode, date) => api.put(`/bonus-ineligible/settings/${regionCode}/off`, null, { params: { date } });
export const getSSNForUpdate = (driverId) => api.get(`/driver-services/drivers/${driverId}/ssn-for-update`);
export const geofenceRequestGPS = (shipmentID) => api.post(`/geofence/${shipmentID}/request_gps`);
export const updateGeofenceDropoff = (shipmentID, payload) => api.put(`/geofence/${shipmentID}/dropoff`, payload);
export const getDriverSuspensionEvents = (suspensionId, urlParams) => api.get(`/events/driver-suspension/${suspensionId}`, urlParams);
export const updateLockRoute = (assignmentID, payload) => api.put(`/assignments/${assignmentID}/lock`, payload);
export const getBookingZoneDetail = (bookingSessionId, ticketBookingGroupId) =>
  api.get(`/booking/${bookingSessionId}/groups/${ticketBookingGroupId}/detail`);
export const getGeofencingRadius = (shipmentID) => api.get(`/shipments/${shipmentID}/geofencing-radius`);
export const getSupportedLanguages = () => api.get('messenger/support_languages');
export const getMetadata = (owner, scope, cache = false) => api.get(`metadata/${owner}/${scope}?cache=${cache}`);
export const getShipmentPins = (shipmentID, assignment_id) => api.get(`/shipments/${shipmentID}/pins`, { assignment_id });
export const updateDispatchPin = (shipmentID, payload) => api.post(`/shipments/${shipmentID}/dispatch-pin`, payload);
export const getPinHistories = (shipmentID) => api.get(`/shipments/${shipmentID}/pin-histories`);
export const repriceAssignment = (payload) => api.post(`/assignments/save-repricing`, payload);
export const getPrice = (assignmentId, payload) => api.post(`/assignments/${assignmentId}/evaluate-cost`, payload);
export const getRepricingDetail = (assignmentId) => api.get(`/assignments/${assignmentId}/repricing-detail`);

export const feedbackAccessCode = (payload) => api.post(`/shipments/feedback-access-code`, payload);

export const getPickupETASession = (payload) => api.get(`/pickup-eta/sessions`, payload);
export const updatePickupETASession = (payload) => api.put(`/pickup-eta/sessions`, payload);
export const addPickupETAWindow = (payload) => api.put(`/pickup-eta/window`, payload);
export const updateNumSlot = (pickupSessionId, payload) => api.put(`/pickup-eta/window/${pickupSessionId}/num-slot`, payload);
export const getPickupETAConfig = (region) => api.get(`/pickup-eta/region/${region}/is-enable`);
export const disablePickupWindows = (payload) => api.post(`/pickup-eta/window/bulk-delete`, payload);
export const enablePickupWindows = (payload) => api.put(`/pickup-eta/windows/enable`, payload);
export const getPickupETAEvents = (pickupSessionId) => api.get(`/pickup-eta/session/${pickupSessionId}/events`);
export const addPickupWindows = (payload) => api.post(`/pickup-eta/window/bulk-add`, payload);
export const createPickupSession = (payload) => api.post(`/pickup-eta/session`, payload);

export const getMetadataForDelivery = (targetPrefix, targetID) => api.get(`/metadata/${targetPrefix}_${targetID}/DELIVERY`);

export const getPodReviews = (shipmentId, stopId) => api.get(`/shipments/${shipmentId}/pod_reviews/${stopId}`);
export const getShipmentAnnotation = (shipmentId, stopId) => api.get(`/shipments/${shipmentId}/annotation/${stopId}`);

export default api;
